import { HttpClient, HttpEventType, HttpParams } from '@angular/common/http';
import { Injectable, NgZone, signal } from '@angular/core';
import { filter, firstValueFrom, map, Subject, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { ChatHistory, ChatMessage, ChatSenderType, ChatWhatsappResult, IncidentChatMessageCreated, PendingChatMessage, ViewedChatMessage, ChatEscalation, ChatEscalationResult } from '../models/chat';

interface ChatUpdate {
  incidentId: number;
  chatSenderTypeId: ChatSenderType;
  clientMessageId?: string;
  progress?: number;
  savedMessage?: ChatMessage;
  fleetChatUnavailable?: boolean;
}

type StoredChatMessageState = Omit<ViewedChatMessage, 'readDate'> & { readDate?: string };

@Injectable({ providedIn: 'root' })
export class ChatService {
  readonly updates = new Subject<ChatUpdate>();
  readonly messageCreated = new Subject<IncidentChatMessageCreated & { viewerType?: ChatSenderType }>();
  readonly socketOpened = new Subject<void>();
  readonly escalationChanges = new Subject<ChatEscalation>();
  private escalations = new Map<number, ChatEscalation>();
  private acknowledgedEscalations = new Set<number>();
  private escalationVersion = signal(0);
  readonly viewedMessages: ViewedChatMessage[] = [];
  readStateNotice = '';
  private unreadMessages = new Map<string, Set<number>>();
  private unreadVersion = signal(0);
  private readStateLoads = new Map<string, Promise<void>>();
  private readStateWrites = Promise.resolve();
  private dbPromise?: Promise<IDBDatabase>;
  private processing = new Set<string>();
  private fleetAccess = new Set<number>();
  private pushRegistrations = new Map<string, Promise<number | undefined>>();

  constructor(private http: HttpClient, private zone: NgZone) {}

  hasEscalation(incidentId: number) {
    this.escalationVersion();
    return [...this.escalations.values()].some(e => e.incidentId === incidentId);
  }

  trackEscalations(escalations: ChatEscalation[]) {
    for (const escalation of escalations) {
      if (escalation.escalationAcknowledgedDate) {
        this.acknowledgedEscalations.add(escalation.chatMessageId);
        this.escalations.delete(escalation.chatMessageId);
        this.escalationChanges.next(escalation);
      } else if (!this.acknowledgedEscalations.has(escalation.chatMessageId) && !this.escalations.has(escalation.chatMessageId)) {
        this.escalations.set(escalation.chatMessageId, escalation);
        this.escalationChanges.next(escalation);
      }
    }
    this.escalationVersion.update(value => value + 1);
  }

  trackChatEscalations(messages: ChatMessage[]) {
    this.trackEscalations(messages.filter(m => m.escalatedDate).map(m => ({ ...m, escalatedDate: m.escalatedDate! })));
  }

  escalate(incidentId: number, chatMessageId: number) {
    return this.http.post<ChatEscalationResult>(`${environment.apiUrl}v1/incident/${incidentId}/chat/messages/${chatMessageId}/escalate`,
      { chatSenderTypeId: ChatSenderType.ServiceProvider });
  }

  acknowledgeEscalations(incidentId: number, messages: ChatMessage[]) {
    return this.http.post<ChatMessage[]>(`${environment.apiUrl}v1/incident/${incidentId}/chat/escalations/acknowledge`,
      { chatMessageIds: messages.map(m => m.chatMessageId) }).pipe(tap(saved => this.trackChatEscalations(saved)));
  }

  registerSenderPushSubscription(incidentId: number, sender: ChatSenderType, registration: Promise<number | undefined>) {
    const key = `pushSubscription:${incidentId}:${sender}`;
    // Reuse the existing IndexedDB store; numeric incident keys still hold chat history.
    this.pushRegistrations.set(key, registration.then(async id => {
      if (typeof id === 'number' && Number.isSafeInteger(id) && id > 0 && id <= 2147483647) {
        try { await this.transaction('history', 'readwrite', s => s.put(id, key)); }
        catch { /* Keep the ID in memory when browser storage cannot retain it. */ }
        return id;
      }
      return this.storedPushSubscription(key);
    }, () => this.storedPushSubscription(key)));
  }

  private async storedPushSubscription(key: string): Promise<number | undefined> {
    try {
      const id = await this.transaction<number | undefined>('history', 'readonly', s => s.get(key));
      return typeof id === 'number' && Number.isSafeInteger(id) && id > 0 && id <= 2147483647 ? id : undefined;
    } catch { return undefined; }
  }

  notifyMessageCreated(update: IncidentChatMessageCreated, viewer: ChatSenderType) {
    const viewed = this.viewedMessages.some(m => m.incidentId === update.incidentId &&
      m.viewerType === viewer && m.chatMessageId === update.chatMessageId);
    if (!viewed) {
      const key = `${update.incidentId}:${viewer}`;
      const unread = this.unreadMessages.get(key) ?? new Set<number>();
      unread.add(update.chatMessageId);
      this.unreadMessages.set(key, unread);
    }
    this.unreadVersion.update(version => version + 1);
    const saved = this.saveReadState(update.incidentId, viewer, [update.chatMessageId]);
    this.messageCreated.next({ ...update, viewerType: viewer });
    return saved;
  }

  unreadCount(incidentId: number, viewer: ChatSenderType): number {
    this.unreadVersion();
    return this.unreadMessages.get(`${incidentId}:${viewer}`)?.size ?? 0;
  }

  markViewed(incidentId: number, viewer: ChatSenderType, messages: ChatMessage[]) {
    const readDate = new Date().toISOString();
    const viewedIds = new Set(this.viewedMessages
      .filter(m => m.incidentId === incidentId && m.viewerType === viewer).map(m => m.chatMessageId));
    const unread = this.unreadMessages.get(`${incidentId}:${viewer}`);
    for (const message of messages) {
      if (message.incidentId !== incidentId) continue;
      if (!viewedIds.has(message.chatMessageId)) {
        this.viewedMessages.push({ incidentId, chatMessageId: message.chatMessageId, viewerType: viewer, readDate });
        viewedIds.add(message.chatMessageId);
      }
      unread?.delete(message.chatMessageId);
    }
    this.unreadVersion.update(version => version + 1);
    return this.saveReadState(incidentId, viewer, messages.filter(m => m.incidentId === incidentId).map(m => m.chatMessageId));
  }

  trackHistory(incidentId: number, viewer: ChatSenderType, messages: ChatMessage[]) {
    const viewedIds = new Set(this.viewedMessages
      .filter(m => m.incidentId === incidentId && m.viewerType === viewer).map(m => m.chatMessageId));
    const key = `${incidentId}:${viewer}`;
    const unread = this.unreadMessages.get(key) ?? new Set<number>();
    const ids = messages.filter(m => m.incidentId === incidentId).map(m => m.chatMessageId);
    for (const id of ids) if (!viewedIds.has(id)) unread.add(id);
    this.unreadMessages.set(key, unread);
    this.unreadVersion.update(version => version + 1);
    return this.saveReadState(incidentId, viewer, ids);
  }

  loadReadState(incidentId: number, viewer: ChatSenderType): Promise<void> {
    const key = `${incidentId}:${viewer}`;
    let loading = this.readStateLoads.get(key);
    if (!loading) {
      loading = this.transaction<StoredChatMessageState[]>('readState', 'readonly',
        s => s.index('scope').getAll([incidentId, viewer])).then(records => {
        const unread = this.unreadMessages.get(key) ?? new Set<number>();
        for (const record of records) {
          const viewed = this.viewedMessages.find(m => m.incidentId === incidentId &&
            m.viewerType === viewer && m.chatMessageId === record.chatMessageId);
          if (record.readDate) {
            if (viewed) viewed.readDate = record.readDate;
            else this.viewedMessages.push(record as ViewedChatMessage);
            unread.delete(record.chatMessageId);
          } else if (!viewed) unread.add(record.chatMessageId);
        }
        this.unreadMessages.set(key, unread);
        this.unreadVersion.update(version => version + 1);
        this.readStateNotice = '';
      }).catch(() => {
        this.readStateLoads.delete(key);
        this.readStateNotice = 'Read status could not be restored from this browser.';
      });
      this.readStateLoads.set(key, loading);
    }
    return loading;
  }

  private saveReadState(incidentId: number, viewer: ChatSenderType, ids: number[]): Promise<void> {
    if (!ids.length) return this.readStateWrites;
    this.readStateWrites = this.readStateWrites.then(async () => {
      await this.loadReadState(incidentId, viewer);
      const db = await this.database();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction('readState', 'readwrite');
        const store = tx.objectStore('readState');
        for (const id of new Set(ids)) {
          const request = store.get([incidentId, viewer, id]);
          request.onsuccess = () => {
            // Never downgrade a stored read message or replace its first read timestamp.
            if (request.result?.readDate) return;
            const viewed = this.viewedMessages.find(m => m.incidentId === incidentId &&
              m.viewerType === viewer && m.chatMessageId === id);
            store.put({ incidentId, viewerType: viewer, chatMessageId: id, readDate: viewed?.readDate });
          };
        }
        tx.oncomplete = () => resolve();
        tx.onabort = () => reject(tx.error);
        tx.onerror = () => reject(tx.error);
      });
      this.readStateNotice = '';
    }).catch(() => { this.readStateNotice = 'Read status could not be saved in this browser.'; });
    return this.readStateWrites;
  }

  history(incidentId: number, sender: ChatSenderType, beforeMessageId?: number) {
    let params = new HttpParams().set('limit', 50).set('chatSenderTypeId', sender);
    if (beforeMessageId) params = params.set('beforeMessageId', beforeMessageId);
    return this.http.get<ChatHistory>(this.url(incidentId), { params });
  }

  private url(incidentId: number) {
    return `${environment.apiUrl}v1/incident/${incidentId}/chat/messages`;
  }

  sendToSelf(incidentId: number, chatMessageIds: number[]) {
    return this.http.post<ChatWhatsappResult[]>(`${environment.apiUrl}v1/incident/${incidentId}/chat/send-to-self`, { chatMessageIds });
  }

  attachment(incidentId: number, chatMessageId: number) {
    return this.http.get(`${this.url(incidentId)}/${chatMessageId}/attachment`, { responseType: 'blob' });
  }

  setFleetAccess(incidentId: number, allowed: boolean) {
    // Keep the loaded incident's permission through connection loss.
    if (allowed) this.fleetAccess.add(incidentId);
    else this.fleetAccess.delete(incidentId);
  }

  private canProcess(incidentId: number, sender: ChatSenderType) {
    return navigator.onLine && (sender !== ChatSenderType.Fleet || this.fleetAccess.has(incidentId));
  }

  private database(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      // Keep large chat blobs and their lifecycle independent of the proof-of-work image queue.
      this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open('MaintenanceIncidentChat', 2);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains('outbox')) db.createObjectStore('outbox', { keyPath: 'clientMessageId' });
          if (!db.objectStoreNames.contains('history')) db.createObjectStore('history');
          if (!db.objectStoreNames.contains('readState')) {
            db.createObjectStore('readState', { keyPath: ['incidentId', 'viewerType', 'chatMessageId'] })
              .createIndex('scope', ['incidentId', 'viewerType']);
          }
        };
        request.onsuccess = () => {
          const db = request.result;
          db.onversionchange = () => { db.close(); this.dbPromise = undefined; };
          resolve(db);
        };
        request.onerror = () => reject(request.error);
      }).catch(error => { this.dbPromise = undefined; throw error; });
    }
    return this.dbPromise;
  }

  private async transaction<T>(storeName: string, mode: IDBTransactionMode,
                               action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const db = await this.database();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(storeName, mode);
      const request = action(tx.objectStore(storeName));
      // Resolve only after the transaction commits: quota/commit failures must keep the draft visible.
      tx.oncomplete = () => resolve(request.result);
      tx.onabort = () => reject(tx.error || new Error('Could not save chat locally'));
      tx.onerror = () => reject(tx.error);
    });
  }

  async pending(incidentId: number, sender: ChatSenderType): Promise<PendingChatMessage[]> {
    const all = await this.transaction<PendingChatMessage[]>('outbox', 'readonly', s => s.getAll());
    return all.filter(m => m.incidentId === incidentId && m.chatSenderTypeId === sender)
      .sort((a, b) => a.createdDate.localeCompare(b.createdDate));
  }

  async enqueue(items: PendingChatMessage[]): Promise<void> {
    for (const item of items) {
      if (item.senderPushSubscriptionId === undefined && item.chatSenderTypeId !== ChatSenderType.Callcentre) {
        item.senderPushSubscriptionId = await this.storedPushSubscription(`pushSubscription:${item.incidentId}:${item.chatSenderTypeId}`);
      }
    }
    const db = await this.database();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('outbox', 'readwrite');
      for (const item of items) tx.objectStore('outbox').add(item);
      tx.oncomplete = () => resolve();
      tx.onabort = () => reject(tx.error || new Error('Could not save chat locally'));
      tx.onerror = () => reject(tx.error);
    });
    if (items.length) this.updates.next(items[0]);
  }

  private async save(item: PendingChatMessage) {
    await this.transaction('outbox', 'readwrite', s => s.put(item));
    this.updates.next(item);
  }

  private async updatePending(id: string, change: (current: PendingChatMessage | undefined) => PendingChatMessage | undefined) {
    const db = await this.database();
    return new Promise<PendingChatMessage | undefined>((resolve, reject) => {
      const tx = db.transaction('outbox', 'readwrite');
      const store = tx.objectStore('outbox');
      const request = store.get(id);
      let updated: PendingChatMessage | undefined;
      let failure: unknown;
      request.onsuccess = () => {
        try {
          updated = change(request.result);
          if (updated) store.put(updated); else store.delete(id);
        } catch (error) { failure = error; tx.abort(); }
      };
      tx.oncomplete = () => resolve(updated);
      tx.onabort = () => reject(failure || tx.error);
      tx.onerror = () => reject(tx.error);
    });
  }

  async remove(item: PendingChatMessage) {
    await this.updatePending(item.clientMessageId, current => {
      if (current?.status === 'sending') throw new Error('This message is already uploading');
      return undefined;
    });
    this.updates.next(item);
  }

  async cached(incidentId: number): Promise<ChatHistory | undefined> {
    return this.transaction('history', 'readonly', s => s.get(incidentId));
  }

  async cache(incidentId: number, history: ChatHistory) {
    await this.transaction('history', 'readwrite', s => s.put(history, incidentId));
  }

  async retry(item: PendingChatMessage) {
    await this.updatePending(item.clientMessageId, current => current && current.status !== 'sending'
      ? { ...current, status: 'pending', error: undefined } : current);
    this.updates.next(item);
    await this.process(item.incidentId, item.chatSenderTypeId);
  }

  async process(incidentId: number, sender: ChatSenderType): Promise<void> {
    const key = `${incidentId}:${sender}`;
    if (this.processing.has(key) || !this.canProcess(incidentId, sender)) return;
    this.processing.add(key);
    try {
      if (navigator.locks) {
        await navigator.locks.request(`incident-chat:${key}`, () => this.zone.run(() => this.drain(incidentId, sender)));
      } else {
        await this.drain(incidentId, sender);
      }
    } finally { this.processing.delete(key); }
  }

  private async drain(incidentId: number, sender: ChatSenderType) {
    const attempted = new Set<string>();
    while (this.canProcess(incidentId, sender)) {
      // Also recover sends interrupted by browser suspension/reload. Server UUIDs make replay safe.
      const candidate = (await this.pending(incidentId, sender))
        .find(m => m.status !== 'failed' && !attempted.has(m.clientMessageId));
      if (!candidate || !this.canProcess(incidentId, sender)) break;
      attempted.add(candidate.clientMessageId);
      const pushKey = `pushSubscription:${incidentId}:${sender}`;
      const senderSubscriptionId = candidate.senderPushSubscriptionId !== undefined ? candidate.senderPushSubscriptionId
        : sender === ChatSenderType.Callcentre ? undefined
        : await (this.pushRegistrations.get(pushKey) ?? this.storedPushSubscription(pushKey));
      if (!this.canProcess(incidentId, sender)) break;
      // Claim atomically so cancellation cannot race a stale queue snapshot and resurrect a removed send.
      const item = await this.updatePending(candidate.clientMessageId, current => current
        ? { ...current, senderPushSubscriptionId: current.senderPushSubscriptionId === undefined
            ? senderSubscriptionId ?? null : current.senderPushSubscriptionId, status: 'sending', error: undefined } : undefined);
      if (!item) continue;
      if (!this.canProcess(incidentId, sender)) {
        await this.save({ ...item, status: 'pending' });
        break;
      }
      this.updates.next(item);
      try {
        const form = new FormData();
        form.append('clientMessageId', item.clientMessageId);
        form.append('chatSenderTypeId', String(item.chatSenderTypeId));
        if (item.senderPushSubscriptionId != null) form.append('senderPushSubscriptionId', String(item.senderPushSubscriptionId));
        if (item.message) form.append('message', item.message);
        if (item.file) form.append('file', item.file, item.filename || 'Attachment');
        if (item.durationSeconds !== undefined) form.append('durationSeconds', String(item.durationSeconds));
        const message = await firstValueFrom(this.http.post<ChatMessage>(this.url(incidentId), form, {
          observe: 'events', reportProgress: true
        }).pipe(
          tap(event => {
            if (event.type === HttpEventType.UploadProgress) {
              this.updates.next({ ...item, progress: event.total ? Math.round(event.loaded / event.total * 100) : undefined });
            }
          }),
          filter(event => event.type === HttpEventType.Response),
          map(event => event.body!)
        ));
        // Persist the acknowledgement before removing the outbox entry.
        const cached = await this.cached(incidentId);
        const messages = [...(cached?.messages || []).filter(m => m.clientMessageId !== message.clientMessageId), message]
          .sort((a, b) => a.chatMessageId - b.chatMessageId);
        await this.cache(incidentId, { messages, hasMore: cached?.hasMore ?? true });
        await this.transaction('outbox', 'readwrite', s => s.delete(item.clientMessageId));
        this.updates.next({ ...item, savedMessage: message });
      } catch (error: any) {
        const offline = error?.status === 0 || !navigator.onLine;
        const fleetChatUnavailable = sender === ChatSenderType.Fleet && error?.status === 403;
        if (fleetChatUnavailable) this.setFleetAccess(incidentId, false);
        await this.save({ ...item, status: offline || fleetChatUnavailable ? 'pending' : 'failed',
          error: offline ? 'Waiting for a connection. Use Retry if already connected.'
            : typeof error?.error?.detail === 'string' ? error.error.detail : 'Could not send. Please retry.' });
        if (fleetChatUnavailable) this.updates.next({ incidentId, chatSenderTypeId: sender, fleetChatUnavailable: true });
        if (offline || fleetChatUnavailable) break;
      }
    }
  }
}
